import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { howManyFit } from "./nav-fit";
import { Link, useLocation } from "wouter";
import { LogoMark } from "./Logo";
import { AdminSignIn } from "./SignIn";
import {
  SCREENS,
  alertsOf,
  gatewayLine,
  loadConsole,
  type Alert,
  type ConsolePayload,
} from "../api/console";

/**
 * The chrome the five Stitch decks share: a dark status line, a navbar with a
 * command palette and an alert bell, and a footer.
 *
 * The designs fill all three with claims this build has not earned (an enclave,
 * a certification, a latency, a named person as Super Admin). What is drawn
 * instead is what is known: the environment off the binding, the gateway in
 * force, the address that actually signed in, and counts of rows.
 */

function initialsOf(email: string): string {
  const local = email.split("@")[0] ?? email;
  const words = local.split(/[._+-]+/).filter((one) => one.length > 0);
  const first = words[0] ?? local;
  const second = words[1];
  const letters = second === undefined ? first.slice(0, 2) : `${first[0] ?? ""}${second[0] ?? ""}`;
  return letters.toUpperCase() || "AD";
}

/** Null while unknown, false once the gate has actually refused us. */
function useConsole(): { data: ConsolePayload | null; admitted: boolean | null } {
  const [data, setData] = useState<ConsolePayload | null>(null);
  const [admitted, setAdmitted] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    const read = (): void => {
      loadConsole()
        .then((next) => {
          if (!live) return;
          setData(next);
          setAdmitted(true);
        })
        .catch((caught: unknown) => {
          if (!live) return;
          setData(null);
          // A refusal is a different thing from a network failure: one wants a
          // sign in, the other wants another try.
          const why = caught instanceof Error ? caught.message : "";
          setAdmitted(why.includes("refused") || why.includes("not_an_admin") ? false : null);
        });
    };
    read();
    const timer = setInterval(read, 60_000);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, []);
  return { data, admitted };
}

function StatusLine({ data }: { readonly data: ConsolePayload | null }) {
  return (
    <aside className="w-full bg-primary-container text-on-primary-container border-b border-outline/30">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 font-label-sm text-label-sm">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="inline-flex items-center gap-1.5 font-semibold text-secondary-fixed-dim uppercase tracking-wider">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-secondary-fixed-dim" />
            Admin console
          </span>
          <span className="text-outline">·</span>
          <span className="text-inverse-on-surface">
            {data === null ? "reading the database" : data.environment || "environment unnamed"}
          </span>
          <span className="text-outline">·</span>
          <span className="hidden sm:inline">Every privileged read is written to the ledger</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="hidden md:inline">
            {data === null ? "currency unread" : `${data.currency} only, whole paise`}
          </span>
          <span className="text-outline hidden md:inline">·</span>
          <span className={data?.gateway.movesRealMoney === true ? "text-error" : ""}>
            {data === null ? "gateway unread" : gatewayLine(data)}
          </span>
          <span className="text-outline">·</span>
          <span className="text-inverse-on-surface tabular-nums">
            {data === null
              ? "ledger unread"
              : `${data.counts.ledgerRows.toLocaleString("en-IN")} ledger ${data.counts.ledgerRows === 1 ? "row" : "rows"}`}
          </span>
        </div>
      </div>
    </aside>
  );
}

function AlertList({
  alerts,
  known,
  onGo,
}: {
  readonly alerts: readonly Alert[];
  /** False until the console has read a row. An empty list is not "nothing". */
  readonly known: boolean;
  readonly onGo: () => void;
}) {
  if (!known) {
    return (
      <p className="p-4 font-body-sm text-body-sm text-on-surface-variant">
        Reading the counts.
      </p>
    );
  }
  if (alerts.length === 0) {
    return (
      <p className="p-4 font-body-sm text-body-sm text-on-surface-variant">
        Nothing needs attention. This list is counts of rows, not a monitor: nothing here pages
        anybody and no uptime is measured.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-outline-variant/60">
      {alerts.map((alert) => (
        <li key={alert.key}>
          <Link
            href={alert.href}
            onClick={onGo}
            className="flex items-start gap-3 p-3.5 hover:bg-surface-container-low"
          >
            <span className="mt-0.5 w-2 h-2 rounded-full bg-error shrink-0" aria-hidden="true" />
            <span className="flex flex-col min-w-0">
              <span className="font-body-sm text-body-sm font-semibold text-on-surface">
                {alert.label}
              </span>
              <span className="font-label-sm text-label-sm text-on-surface-variant">
                {alert.detail}
              </span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * The design's search box. It jumps between screens and hands the deck a query
 * to run. It reads nothing itself, so it names nobody and writes no row.
 */
function Palette({ onClose }: { readonly onClose: () => void }) {
  const [, navigate] = useLocation();
  const [typed, setTyped] = useState("");
  const box = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    box.current?.focus();
  }, []);

  const screens = useMemo(() => {
    const needle = typed.trim().toLowerCase();
    if (needle.length === 0) return SCREENS;
    return SCREENS.filter(
      (one) =>
        one.label.toLowerCase().includes(needle) || one.hint.toLowerCase().includes(needle),
    );
  }, [typed]);

  const term = typed.trim();

  function go(href: string): void {
    navigate(href);
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-inverse-surface/50 flex items-start justify-center p-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Jump to a screen or search candidates"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl bg-surface-container-lowest border border-outline-variant rounded-xl shadow-lg overflow-hidden">
        <input
          ref={box}
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") onClose();
            if (event.key === "Enter" && term.length > 0) {
              go(`/candidates?q=${encodeURIComponent(term)}`);
            }
          }}
          placeholder="Jump to a screen, or type an address to find a candidate"
          className="w-full px-4 py-3.5 bg-surface-container-lowest border-b border-outline-variant font-body-md text-body-md text-on-surface placeholder:text-on-surface-variant focus:outline-none"
        />
        <ul className="max-h-80 overflow-y-auto divide-y divide-outline-variant/50">
          {term.length > 0 && (
            <li>
              <button
                type="button"
                onClick={() => go(`/candidates?q=${encodeURIComponent(term)}`)}
                className="w-full text-left px-4 py-3 hover:bg-surface-container-low"
              >
                <span className="font-body-sm text-body-sm font-semibold text-on-surface block">
                  Search the roster for {term}
                </span>
                <span className="font-label-sm text-label-sm text-on-surface-variant">
                  A list read. It names nobody in the ledger and writes nothing.
                </span>
              </button>
            </li>
          )}
          {screens.map((one) => (
            <li key={one.href}>
              <button
                type="button"
                onClick={() => go(one.href)}
                className="w-full text-left px-4 py-3 hover:bg-surface-container-low"
              >
                <span className="font-body-sm text-body-sm font-semibold text-on-surface block">
                  {one.label}
                </span>
                <span className="font-label-sm text-label-sm text-on-surface-variant">
                  {one.hint}
                </span>
              </button>
            </li>
          ))}
          {screens.length === 0 && term.length === 0 && (
            <li className="px-4 py-3 font-body-sm text-body-sm text-on-surface-variant">
              No screen matches.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

/** One nav cell. The measuring copy uses the same classes or the widths lie. */
function navCell(active: boolean): string {
  return [
    "px-2.5 py-2 rounded-lg font-body-sm text-body-sm transition-colors whitespace-nowrap shrink-0",
    active
      ? "bg-primary text-on-primary font-semibold"
      : "text-on-surface-variant hover:text-on-surface hover:bg-surface-container",
  ].join(" ");
}

/** The screens that did not fit. Drawn only when something did not. */
function ScreenMenu({
  items,
  location,
}: {
  readonly items: readonly (typeof SCREENS)[number][];
  readonly location: string;
}) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const current = items.find((one) => one.href === location);

  useEffect(() => {
    if (!open) return;
    const shut = (): void => setOpen(false);
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };
    window.addEventListener("click", shut);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", shut);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div className="relative shrink-0" onClick={(event) => event.stopPropagation()}>
      <button
        ref={trigger}
        className={`${navCell(current !== undefined)} flex items-center gap-1 min-h-[44px]`}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={current === undefined ? "More screens" : `Menu, you are on ${current.label}`}
        onClick={() => setOpen((was) => !was)}
      >
        More
        <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
          expand_more
        </span>
      </button>

      {open ? (
        <div
          className="absolute right-0 top-full mt-1 w-56 rounded-xl bg-surface-container-lowest shadow-md border border-outline-variant/50 py-1 z-50"
          role="menu"
        >
          {items.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              role="menuitem"
              aria-current={location === item.href ? "page" : undefined}
              className={[
                "flex items-center w-full px-3 min-h-[44px] font-body-sm text-body-sm",
                location === item.href
                  ? "bg-surface-container text-primary font-semibold"
                  : "text-on-surface hover:bg-surface-container-low",
              ].join(" ")}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function AdminShell({ children }: { readonly children: ReactNode }) {
  const bar = useRef<HTMLElement | null>(null);
  const ghost = useRef<HTMLDivElement | null>(null);
  const [fitted, setFitted] = useState(SCREENS.length);

  const measure = useCallback(() => {
    const nav = bar.current;
    const row = ghost.current;
    if (nav === null || row === null) return;
    const widths = [...row.querySelectorAll<HTMLElement>("[data-nav-cell]")].map(
      (cell) => cell.getBoundingClientRect().width,
    );
    const more = row.querySelector<HTMLElement>("[data-nav-trigger]");
    setFitted(
      howManyFit(widths, nav.clientWidth, more === null ? 0 : more.getBoundingClientRect().width),
    );
  }, []);

  useLayoutEffect(() => {
    measure();
    const nav = bar.current;
    if (nav === null || typeof ResizeObserver === "undefined") return;
    const watch = new ResizeObserver(() => measure());
    watch.observe(nav);
    return () => watch.disconnect();
  }, [measure]);

  const inline = SCREENS.slice(0, fitted);
  const folded = SCREENS.slice(fitted);

  const [location] = useLocation();
  const { data, admitted } = useConsole();

  const [palette, setPalette] = useState(false);
  const [bell, setBell] = useState(false);

  const alerts = useMemo(() => (data === null ? [] : alertsOf(data.counts)), [data]);

  const onKey = useCallback((event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      setPalette((open) => !open);
    }
    if (event.key === "Escape") {
      setPalette(false);
      setBell(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onKey]);

  useEffect(() => {
    setBell(false);
  }, [location]);

  // The console is for admins. Somebody who is not one gets the door, not five
  // screens of refusals.
  //
  // AFTER THE HOOKS, NOT BEFORE THEM. This return used to sit above two
  // useState calls and three useEffects, which is a conditional hook: the
  // count changed between the render where `admitted` was null and the one
  // where it was false, React threw on the mismatch, and the whole tree
  // unmounted. What a signed out visitor saw was not the sign in form, it was
  // an empty page.
  if (admitted === false) return <AdminSignIn />;

  return (
    <div className="min-h-screen bg-background font-body-md text-on-surface antialiased flex flex-col">
      <StatusLine data={data} />

      <header className="sticky top-0 z-50 w-full bg-surface-container-lowest/95 backdrop-blur-xl border-b border-outline-variant">
        <div className="h-16 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-space-sm">
          <Link
            href="/observability"
            className="flex items-center gap-2.5 shrink-0"
            aria-label="PrepHike admin console, observability"
          >
            {/* THE SAME MARK THE DASHBOARD DRAWS. A "PH" square here made the
                console look like a different product from the one it operates. */}
            <LogoMark className="w-9 h-9 rounded-lg shadow-sm" />
            <span className="font-headline-sm text-headline-sm font-bold tracking-tight text-on-surface">
              PrepHike
            </span>
            <span className="hidden 2xl:inline px-2 py-0.5 rounded border border-outline-variant bg-surface-container text-on-surface-variant font-label-sm text-label-sm uppercase tracking-wider font-semibold">
              Admin console
            </span>
          </Link>

          <nav
            aria-label="Admin screens"
            ref={bar}
            className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden"
          >
            {inline.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                aria-current={location === item.href ? "page" : undefined}
                className={navCell(location === item.href)}
              >
                {item.label}
              </Link>
            ))}
            <ScreenMenu items={folded} location={location} />
          </nav>

          {/*
            The measuring copy. A clipped render of the real labels, so what
            fits is read rather than guessed. `aria-hidden` and inert: it must
            never be reachable or announced.
          */}
          <div
            ref={ghost}
            aria-hidden="true"
            className="absolute -left-[9999px] top-0 flex items-center gap-1 pointer-events-none"
          >
            {SCREENS.map((item) => (
              <span key={item.href} data-nav-cell className={navCell(false)}>
                {item.label}
              </span>
            ))}
            <span data-nav-trigger className={navCell(false)}>
              More
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setPalette(true)}
              className="hidden 2xl:flex items-center gap-2 w-56 px-3 py-1.5 rounded-lg bg-surface-container-low border border-outline-variant text-on-surface-variant hover:bg-surface-container transition-colors"
            >
              <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
                search
              </span>
              <span className="font-body-sm text-body-sm flex-1 text-left truncate">
                Search screens and candidates
              </span>
              <kbd className="font-label-sm text-label-sm px-1.5 py-0.5 rounded border border-outline-variant bg-surface-container-lowest">
                Ctrl K
              </kbd>
            </button>
            <button
              type="button"
              onClick={() => setPalette(true)}
              aria-label="Search screens and candidates"
              className="2xl:hidden w-9 h-9 rounded-lg border border-outline-variant bg-surface-container-low text-on-surface-variant flex items-center justify-center"
            >
              <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                search
              </span>
            </button>

            <div className="relative">
              <button
                type="button"
                onClick={() => setBell((open) => !open)}
                aria-expanded={bell}
                aria-label={
                  data === null
                    ? "Reading what needs attention"
                    : alerts.length === 0
                      ? "Nothing needs attention"
                      : `${alerts.length} things need attention`
                }
                className="relative w-9 h-9 rounded-lg border border-outline-variant bg-surface-container-low text-on-surface-variant hover:text-on-surface flex items-center justify-center"
              >
                <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                  notifications
                </span>
                {alerts.length > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-error text-on-error font-label-sm text-[11px] font-bold flex items-center justify-center tabular-nums">
                    {alerts.length}
                  </span>
                )}
              </button>
              {bell && (
                <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-surface-container-lowest border border-outline-variant rounded-xl shadow-lg overflow-hidden z-50">
                  <div className="px-4 py-2.5 border-b border-outline-variant bg-surface-container-low font-label-sm text-label-sm uppercase tracking-wider text-on-surface-variant">
                    Counted from the database, just now
                  </div>
                  <AlertList alerts={alerts} known={data !== null} onGo={() => setBell(false)} />
                </div>
              )}
            </div>

            <span className="flex items-center gap-2 pl-1">
              <span
                aria-hidden="true"
                className="w-8 h-8 rounded-full bg-primary text-on-primary flex items-center justify-center font-label-sm text-label-sm font-bold"
              >
                {data === null ? "??" : initialsOf(data.admin)}
              </span>
              <span className="hidden 2xl:flex flex-col leading-tight min-w-0">
                <span className="font-label-md text-label-md font-semibold text-on-surface max-w-[11rem] truncate">
                  {data === null ? "Reading the session" : data.admin}
                </span>
                <span className="font-label-sm text-label-sm text-on-surface-variant">
                  On the admin allowlist
                </span>
              </span>
            </span>
          </div>
        </div>

      </header>

      <main className="w-full flex-1">{children}</main>

      <footer className="w-full border-t border-outline-variant/60 bg-surface-container-low mt-space-lg">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-2 font-label-sm text-label-sm text-on-surface-variant">
          <span>
            One database, read live. The admin ledger is append only and is not tamper evident:
            these are ordinary rows.{" "}
            <Link href="/audit" className="inline-flex items-center min-h-[24px] py-1 underline text-on-surface">
              See the ledger
            </Link>
            {" · "}
            {/* The five per screen tables, in the one place somebody would go
                looking for them rather than under a screen they are using. */}
            <Link href="/not-built" className="inline-flex items-center min-h-[24px] py-1 underline text-on-surface">
              Not built, and why
            </Link>
          </span>
          <span className="tabular-nums">
            {data === null
              ? "Chrome counts unread"
              : `${data.environment || "environment unnamed"}, ${data.counts.candidates.toLocaleString("en-IN")} accounts, ${data.currency} only`}
          </span>
        </div>
      </footer>

      {palette && <Palette onClose={() => setPalette(false)} />}
    </div>
  );
}
